import type {
  ApiError,
  MintTokenResponse,
} from '@knockonwood/arcade-card-shared';
import { ArcadeCardError } from './errors.js';
import type { TokenProvider } from './index.js';

interface AuthStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

interface BrowserLocation {
  href: string;
  assign(url: string): void;
}

interface BrowserHistory {
  replaceState(data: unknown, unused: string, url?: string | URL | null): void;
}

export interface RedirectTokenProviderConfig {
  /** Catalog id of the standalone world, game, or app. */
  experienceId: string;
  /** Cashier origin, e.g. https://api.knockonwood.studio. */
  apiBase: string;
  /** Must exactly match one of this client's catalog redirect URIs. */
  redirectUri: string;
  /** Browser dependencies are injectable for tests and unusual web runtimes. */
  storage?: AuthStorage;
  location?: BrowserLocation;
  history?: BrowserHistory;
}

interface PendingAuthorization {
  state: string;
  verifier: string;
}

interface CachedToken extends MintTokenResponse {
  expiresAt: number;
}

/**
 * Token provider for a standalone experience on a different registrable
 * domain. It performs an OAuth-style authorization-code + PKCE redirect,
 * exchanges the one-time code, cleans the callback URL, and caches only the
 * resulting short-lived experience token in sessionStorage.
 */
export function createRedirectTokenProvider(
  config: RedirectTokenProviderConfig,
): TokenProvider {
  const apiBase = config.apiBase.replace(/\/$/, '');
  const location = config.location ?? globalThis.location;
  const history = config.history ?? globalThis.history;
  const storage = config.storage ?? globalThis.sessionStorage;
  const key = `kow.arcade-pass.redirect.${config.experienceId}.v1`;
  const tokenKey = `${key}.token`;

  return async () => {
    const cached = readJson<CachedToken>(storage, tokenKey);
    if (cached && cached.expiresAt > Date.now() + 30_000) {
      return {
        token: cached.token,
        expiresIn: Math.max(1, Math.floor((cached.expiresAt - Date.now()) / 1000)),
      };
    }

    const current = new URL(location.href);
    const code = current.searchParams.get('code');
    const callbackState = current.searchParams.get('state');
    if (code || callbackState) {
      const pending = readJson<PendingAuthorization>(storage, key);
      if (!code || !callbackState || !pending || callbackState !== pending.state) {
        storage.removeItem(key);
        cleanCallbackUrl(current, history);
        throw new ArcadeCardError(
          'UNAUTHORIZED',
          'Arcade Pass authorization state did not match',
          401,
        );
      }

      const response = await fetch(`${apiBase}/v1/pass/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          grant_type: 'authorization_code',
          client_id: config.experienceId,
          redirect_uri: config.redirectUri,
          code,
          code_verifier: pending.verifier,
        }),
      });
      if (!response.ok) {
        const error = (await response.json().catch(() => null)) as ApiError | null;
        storage.removeItem(key);
        cleanCallbackUrl(current, history);
        throw new ArcadeCardError(
          error?.code ?? 'UNAUTHORIZED',
          error?.message ?? 'Could not exchange Arcade Pass authorization code',
          response.status,
        );
      }

      const token = (await response.json()) as MintTokenResponse;
      storage.removeItem(key);
      storage.setItem(
        tokenKey,
        JSON.stringify({ ...token, expiresAt: Date.now() + token.expiresIn * 1000 }),
      );
      cleanCallbackUrl(current, history);
      return token;
    }

    const verifier = randomBase64Url(32);
    const state = randomBase64Url(32);
    const challenge = await sha256Base64Url(verifier);
    storage.setItem(key, JSON.stringify({ state, verifier } satisfies PendingAuthorization));

    const authorize = new URL(`${apiBase}/v1/pass/authorize`);
    authorize.searchParams.set('response_type', 'code');
    authorize.searchParams.set('client_id', config.experienceId);
    authorize.searchParams.set('redirect_uri', config.redirectUri);
    authorize.searchParams.set('state', state);
    authorize.searchParams.set('code_challenge', challenge);
    authorize.searchParams.set('code_challenge_method', 'S256');
    location.assign(authorize.toString());

    // Navigation ends this document. Keeping the promise pending prevents
    // callers from treating the not-yet-authorized SDK as initialized.
    return new Promise<MintTokenResponse>(() => {});
  };
}

function cleanCallbackUrl(url: URL, history: BrowserHistory): void {
  url.searchParams.delete('code');
  url.searchParams.delete('state');
  history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
}

function readJson<T>(storage: AuthStorage, key: string): T | null {
  try {
    const raw = storage.getItem(key);
    return raw ? JSON.parse(raw) as T : null;
  } catch {
    return null;
  }
}

function randomBase64Url(bytes: number): string {
  const value = new Uint8Array(bytes);
  globalThis.crypto.getRandomValues(value);
  return encodeBase64Url(value);
}

async function sha256Base64Url(value: string): Promise<string> {
  const digest = await globalThis.crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(value),
  );
  return encodeBase64Url(new Uint8Array(digest));
}

function encodeBase64Url(value: Uint8Array): string {
  let binary = '';
  for (const byte of value) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
